import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { MyAccount } from '../../../../../models/my-account';
import { AccountService } from '../../../../../services/account/account.service';
import { Association, AssociationAlert, AssociationManager } from '../../../../../models/association';
import { AssociationService } from '../../../../../services/association/association.service';
import { CallbackResult, CallbackResultStatus, dateStringFromISO8601 } from '../../../../../utils';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AddAssociationManagerModalComponent } from './modals/add-association-manager-modal/add-association-manager-modal.component';
import { EditAssociationManagerModalComponent } from './modals/edit-association-manager-modal/edit-association-manager-modal.component';
import { SimpleMessageModalComponent } from '../../../../../components/modals/simple-message-modal/simple-message-modal.component';
import { getAssociationAlertStyle } from '../../../../../utils-models';
import { AddAssociationAlertModalComponent } from './modals/add-association-alert-modal/add-association-alert-modal.component';


enum SubList {
  members = 'members',
  discounts = 'discounts',
  activityLog = 'activity-log',
  entryPasses = 'entry-passes',
}


@Component({
  selector: 'app-association-profile',
  templateUrl: './association-profile.component.html',
  styleUrls: ['./association-profile.component.css']
})
export class AssociationProfileComponent implements OnInit {
  public association: Association = null;
  public associationManagers: AssociationManager[] = null;
  public alerts: AssociationAlert[] = null;
  public subList: SubList = SubList.members;

  public loadingAssociation = true;
  public loadingAssociationManagers = true;
  public loadingAlerts = true;

  // A dict mapping an alert ID to a boolean indicating whether that
  // alert is currently being dismissed.
  public loadingDismissAlert: {[key: number]: boolean} = {};

  public associationNotFound = false;
  public serverError: number = null;

  public myAccount: MyAccount;

  // Make browser history available to the template.
  public history: History;

  // The most recent result returned from a modal or sublist.
  public callbackResult: CallbackResult = null;

  public SubList = SubList;
  public dateStringFromISO8601 = dateStringFromISO8601;
  public getAssociationAlertStyle = getAssociationAlertStyle;
  public CallbackResultStatus = CallbackResultStatus;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private associationService: AssociationService,
    private accountService: AccountService,
    private modalService: NgbModal,
    private ref: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.myAccount = this.accountService.getMyAccount();

    this.route.params.subscribe(routeParams => {
      const idInt = parseInt(routeParams.id, 10);
      if (isNaN(idInt)) {
        this.associationNotFound = true;
        this.loadingAssociation = false;
        return;
      }

      // Make browser history available to the template.
      this.history = history;

      this.loadAssociation(idInt);
      this.loadAssociationManagers(idInt);
      this.loadAlerts(idInt);
    });

    const routeFragment = this.route.snapshot.fragment;
    // @ts-ignore
    if (Object.values(SubList).includes(routeFragment)) {
      // @ts-ignore
      this.subList = routeFragment;
    } else {
      this.subList = SubList.members;
    }
  }

  public loadAssociation(id: number, setLoading=true) {
    if (setLoading) {
      this.loadingAssociation = true;
    }

    this.associationService
      .getAssociation(id)
      .then(result => {
        this.association = result;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.associationNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingAssociation = false; });
  }

  public loadAssociationManagers(associationId: number) {
    this.loadingAssociationManagers = true;

    this.associationService
      .getAssociationManagers(1, 1000, {association_id: associationId})
      .then(result => {
        this.associationManagers = result.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.associationNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingAssociationManagers = false; });
  }

  public loadAlerts(associationId: number) {
    this.loadingAlerts = true;

    this.associationService
      .getAssociationAlerts(associationId, 1, 100)
      .then(result => {
        this.alerts = result.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.associationNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingAlerts = false; });
  }

  public openAddManagerModal() {
    this.callbackResult = null;

    const modalRef = this.modalService.open(AddAssociationManagerModalComponent);
    modalRef.componentInstance.association = this.association;
    modalRef.componentInstance.excludeCustomerIds = [];
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.callbackResult = result;
      this.loadAssociationManagers(this.association.id);
    };
  }

  public openEditManagerModal(associationManager: AssociationManager) {
    this.callbackResult = null;

    const modalRef = this.modalService.open(EditAssociationManagerModalComponent);
    modalRef.componentInstance.associationManager = associationManager;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.callbackResult = result;
      this.loadAssociationManagers(this.association.id);
    };
  }

  public handleSubListResult = (result: CallbackResult) => {
    this.callbackResult = result;
    this.loadAssociation(this.association.id, false);
  }

  public selectSubList(subList: SubList) {
    this.subList = subList;

    document.location.hash = subList;

    // this.router.navigate(
    //   [],
    //   {
    //     fragment: subList,
    //     replaceUrl: true
    //   }
    // );
  }

  public dismissAlert(alert: AssociationAlert) {
    this.callbackResult = null;

    this.loadingDismissAlert[alert.id] = true;

    this.associationService
      .removeAssociationAlert(alert.url)
      .then(() => {
        this.loadAlerts(this.association.id);
      })
      .catch(reason => {
        if (reason.status === 403) {
          const messageModalRef = this.modalService.open(SimpleMessageModalComponent);
          messageModalRef.componentInstance.headerText = 'Request Failed';
          messageModalRef.componentInstance.messageText = 'You do not have permission to dismiss this alert.';
          messageModalRef.componentInstance.messageAlertClass = 'alert-danger';
          return;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingDismissAlert[alert.id] = false; });
  }

  public openAddAlertModal() {
    this.callbackResult = null;

    const modalRef = this.modalService.open(AddAssociationAlertModalComponent);
    modalRef.componentInstance.association = this.association;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.callbackResult = result;
      this.loadAlerts(this.association.id);
    }
  }

}
